'use client';

import { login } from '@/app/signin/actions';
import { MuiButton } from '@/components/button/MuiButton';
import { MuiTextField } from '@/components/textfield/MuiTextField';
import { SigninResolver, SigninType } from '@/schemas/SigninSchema';
import { PORTAL_USE_TYPE } from '@/types/types';
import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { DoubleLoginForm } from './DoubleLoginForm';
import { createPortalSession } from '@/helpers/accountHelper';
import { Loading } from '@/components/loader/Loading';
import { useSearchParams } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { PasswordInputField } from '@/components/textfield/PasswordInputField';
import Logo from '@/../public/logo_normal.png';

export function SigninForm({ useType }: { useType: PORTAL_USE_TYPE }) {
  const { data: session } = useSession();
  const [needDoubleLogin, setNeedDoubleLogin] = useState(false);
  const [loginInfo, setLoginInfo] = useState({
    token: '',
    ewellUserId: NaN,
  });
  const [isLoading, setIsLoading] = useState(false);
  const searchParams = useSearchParams();
  const { setValue, control, handleSubmit, setError } = useForm<SigninType>({
    resolver: SigninResolver,
    defaultValues: { use_type: useType },
  });

  const onSubmit = async (formValue: SigninType) => {
    setIsLoading(true);
    const res = await login({ formValue });
    if (res && res.code === 500) {
      setError('user_id', { type: 'custom', message: '' });
      setError('password', { type: 'custom', message: res.message });
      setIsLoading(false);
      return;
    }
    if (res && !res.token && res.security_code) {
      setLoginInfo({
        token: res.security_code,
        ewellUserId: res.ewell_user_id,
      });
      setNeedDoubleLogin(true);
      setIsLoading(false);
      return;
    }
    const params = new URLSearchParams(searchParams.toString());
    params.set('use_type', useType.toString());
    await createPortalSession(
      searchParams,
      session,
      useType,
      `/recept?${params.toString()}`,
    );
  };

  useEffect(() => {
    setValue('use_type', useType);
  }, [useType, setValue]);

  if (needDoubleLogin) {
    return (
      <>
        <DoubleLoginForm
          useType={useType}
          loginInfo={loginInfo}
          setIsLoading={setIsLoading}
        />
        <Loading isLoading={isLoading} />
      </>
    );
  }
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box display="flex" flexDirection="column" gap={3} mt={-4} mb={4}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box
            component="img"
            src={Logo.src}
            alt="けあログっとロゴ"
            sx={{ maxWidth: '300px', margin: '0 auto' }}
          />
          <Typography
            variant="JP/Heading/2xl/Bold/Tight"
            textAlign="center"
            component="p"
            color="primary"
            sx={{
              '@media (max-width: 600px)': { fontSize: '24px' },
            }}
          >
            {useType === PORTAL_USE_TYPE.REQUEST_USER
              ? '利用者を紹介する'
              : '看護・医療の情報を掲載する'}
          </Typography>
          <Typography
            variant="JP/Heading/s/Bold/Tight"
            textAlign="center"
            component="p"
            sx={{ mb: 5, '@media (max-width: 600px)': { fontSize: '16px' } }}
          >
            {useType === PORTAL_USE_TYPE.REQUEST_USER
              ? '介護・医療の事業者職員としてログイン'
              : '訪問看護ステーションの職員としてログイン'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="JP/Body/m/Medium/None">
            メールアドレス/電話番号/iBowID
          </Typography>
          <MuiTextField
            required
            name="user_id"
            control={control}
            placeholder="メールアドレス/電話番号/iBowID"
          />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="JP/Body/m/Medium/None">パスワード</Typography>
          <PasswordInputField
            id="login-password"
            name="password"
            control={control}
            placeholder="パスワード"
          />
        </Box>

        <MuiButton type="submit" size="large" sx={{ fontSize: '18px' }}>
          ログイン
        </MuiButton>

        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={1.5}
        >
          <MuiButton
            href={`/signup?use_type=${useType}`}
            variant="text"
            color="secondary"
            size="large"
          >
            アカウント登録はこちらから
          </MuiButton>
          <MuiButton
            href={`/signin?use_type=${
              useType === PORTAL_USE_TYPE.REQUEST_USER
                ? PORTAL_USE_TYPE.PORTAL_STAFF
                : PORTAL_USE_TYPE.REQUEST_USER
            }`}
            variant="text"
            color="inherit"
            size="small"
          >
            {useType === PORTAL_USE_TYPE.REQUEST_USER
              ? '訪問看護ステーションの職員としてログインしたい方はこちらから'
              : '介護・医療の事業者職員としてログインしたい方はこちらから'}
          </MuiButton>
          <MuiButton
            href="/password-reset"
            variant="text"
            color="inherit"
            size="small"
          >
            パスワードを忘れた方はこちらから
          </MuiButton>
        </Box>
      </Box>
      <Loading isLoading={isLoading} />
    </form>
  );
}
